import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Button from './Button';
import emailjs from '@emailjs/browser';
import { useLocation } from 'react-router-dom';

// Fonction pour extraire les paramètres de l'URL
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ContactForm: React.FC = () => {
  const form = useRef<HTMLFormElement>(null);
  const query = useQuery();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    guests: '2',
    startDate: '',
    endDate: '',
  });
  
  // Fonction pour convertir une date du format "jour/mois/année" au format "année-mois-jour"
  const convertDateFormat = (dateString: string | null): string => {
    if (!dateString) return '';
    
    const [day, month, year] = dateString.split('/').map(Number);
    // Ajouter un zéro devant le jour et le mois si nécessaire
    const formattedDay = day < 10 ? `0${day}` : `${day}`;
    const formattedMonth = month < 10 ? `0${month}` : `${month}`;
    
    return `${year}-${formattedMonth}-${formattedDay}`;
  };
  
  // Récupérer les paramètres de l'URL et pré-remplir le formulaire
  useEffect(() => {
    const startDate = query.get('startDate');
    const endDate = query.get('endDate');
    const guests = query.get('guests');
    
    if (startDate || endDate || guests) {
      // Convertir les dates au format attendu par les champs de type "date"
      const formattedStartDate = convertDateFormat(startDate);
      const formattedEndDate = convertDateFormat(endDate);
      
      setFormData(prev => ({
        ...prev,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        guests: guests || '2',
        message: startDate && endDate ? 
          `Bonjour, je souhaiterai réserver pour ${guests} ${Number(guests) === 1 ? 'personne' : 'personnes'}.` : 
          prev.message
      }));
    }
  }, []); // Exécuter une seule fois au montage du composant
  
  const [formStatus, setFormStatus] = useState<{
    submitted: boolean;
    success: boolean;
    message: string;
  }>({
    submitted: false,
    success: false,
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation simple
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        submitted: true,
        success: false,
        message: 'Veuillez remplir tous les champs obligatoires.',
      });
      return;
    }
    
    try {
      // Créer un message complet avec toutes les informations du formulaire
      const completeMessage = `
Nom: ${formData.name}
Email: ${formData.email}
Téléphone: ${formData.phone || 'Non spécifié'}
Nombre d'invités: ${formData.guests} ${Number(formData.guests) === 1 ? 'personne' : 'personnes'}
Dates: ${formData.startDate ? new Date(formData.startDate).toLocaleDateString('fr-FR') : 'Non spécifiée'} au ${formData.endDate ? new Date(formData.endDate).toLocaleDateString('fr-FR') : 'Non spécifiée'}

Message original:
${formData.message}
      `;
      
      // Créer une copie du formulaire pour modification
      let result;
      if (form.current) {
        // Trouver le champ message dans le formulaire
        const messageField = form.current.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
        if (messageField) {
          // Sauvegarder la valeur originale
          const originalValue = messageField.value;
          
          // Remplacer par le message complet
          messageField.value = completeMessage;
          
          // Envoyer l'email via EmailJS
          result = await emailjs.sendForm(
            'service_7f3pbd4',  // ID du service
            'template_wh614gm', // ID du modèle
            form.current as HTMLFormElement,
            'LKF-zM80BCJ68hqx9' // Clé publique
          );
          
          // Restaurer la valeur originale
          messageField.value = originalValue;
        } else {
          // Si le champ message n'est pas trouvé, envoyer le formulaire tel quel
          result = await emailjs.sendForm(
            'service_7f3pbd4',  // ID du service
            'template_wh614gm', // ID du modèle
            form.current as HTMLFormElement,
            'LKF-zM80BCJ68hqx9' // Clé publique
          );
        }
      }
      
      if (result && result.text === 'OK') {
        setFormStatus({
          submitted: true,
          success: true,
          message: 'Votre message a été envoyé avec succès. Nous vous contacterons dans les plus brefs délais.',
        });
        
        // Réinitialiser le formulaire en cas de succès
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          guests: '2',
          startDate: '',
          endDate: '',
        });
        
        // Réinitialiser le statut après 5 secondes
        setTimeout(() => {
          setFormStatus({
            submitted: false,
            success: false,
            message: '',
          });
        }, 5000);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du formulaire:', error);
      setFormStatus({
        submitted: true,
        success: false,
        message: 'Une erreur est survenue lors de l\'envoi du message. Veuillez réessayer plus tard.',
      });
    }
  };

  return (
    <FormContainer>
      <FormTitle>Contactez-nous</FormTitle>
      <FormDescription>
        Vous avez des questions ou souhaitez réserver ? N'hésitez pas à nous contacter en remplissant le formulaire ci-dessous.
      </FormDescription>
      
      {formStatus.submitted && (
        <FormMessage $success={formStatus.success}>
          {formStatus.message}
        </FormMessage>
      )}
      
      <Form ref={form} onSubmit={handleSubmit}>
        {/* Champ caché pour le message complet */}
        <input 
          type="hidden" 
          name="full_message" 
          value={`
Email: ${formData.email}
Téléphone: ${formData.phone || 'Non spécifié'}
Nombre d'invités: ${formData.guests} ${Number(formData.guests) === 1 ? 'personne' : 'personnes'}
Dates: ${formData.startDate || 'Non spécifiée'} au ${formData.endDate || 'Non spécifiée'}

Message:
${formData.message}
          `}
        />
        
        <FormGroup>
          <FormLabel htmlFor="name">Nom complet *</FormLabel>
          <FormInput
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </FormGroup>
        
        <FormRow>
          <FormGroup>
            <FormLabel htmlFor="email">Email *</FormLabel>
            <FormInput
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel htmlFor="phone">Téléphone</FormLabel>
            <FormInput
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </FormGroup>
        </FormRow>
        
        <FormRow>
          <FormGroup>
            <FormLabel htmlFor="startDate">Date d'arrivée</FormLabel>
            <FormInput
              type="date"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel htmlFor="endDate">Date de départ</FormLabel>
            <FormInput
              type="date"
              id="endDate"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              min={formData.startDate || new Date().toISOString().split('T')[0]}
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel htmlFor="guests">Nombre d'invités</FormLabel>
            <FormSelect
              id="guests"
              name="guests"
              value={formData.guests}
              onChange={handleChange}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                <option key={num} value={num}>
                  {num} {num === 1 ? 'personne' : 'personnes'}
                </option>
              ))}
            </FormSelect>
          </FormGroup>
        </FormRow>
        
        <FormGroup>
          <FormLabel htmlFor="message">Message *</FormLabel>
          <FormTextarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={5}
            required
          />
        </FormGroup>
        
        <FormButton>
          <Button type="submit" fullWidth>
            Envoyer
          </Button>
        </FormButton>
      </Form>

    </FormContainer>
    
  );
};

const FormContainer = styled.div`
  background-color: var(--color-light);
  padding: 4rem;
  box-shadow: var(--shadow);
`;

const FormTitle = styled.h3`
  font-size: 2.8rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const FormDescription = styled.p`
  text-align: center;
  margin-bottom: 3rem;
  color: var(--color-secondary);
  font-size: 1.6rem;
`;

const AirbnbContainer = styled.div`
  margin-top: 2rem;
  margin-bottom: 2rem;
  width: 100%;
  
  a {
    color: var(--color-accent);
    text-decoration: underline;
    font-size: 1.4rem;
    display: block;
    margin-bottom: 0.5rem;
    
    &:hover {
      color: var(--color-light);
    }
  }
`;


interface FormMessageProps {
  $success: boolean;
}

const FormMessage = styled.div<FormMessageProps>`
  padding: 1.5rem;
  margin-bottom: 2rem;
  border-radius: 0;
  background-color: ${({ $success }) => $success ? '#e6f7e6' : '#fce8e8'};
  color: ${({ $success }) => $success ? '#2e7d32' : '#c62828'};
  border-left: 4px solid ${({ $success }) => $success ? '#2e7d32' : '#c62828'};
  font-size: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FormGroup = styled.div`
  flex: 1;
`;

const FormRow = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.8rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 1.2rem;
  font-size: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background-color: white;
  font-family: var(--font-secondary);
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 1.2rem;
  font-size: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background-color: white;
  font-family: var(--font-secondary);
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 1.2rem;
  font-size: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background-color: white;
  font-family: var(--font-secondary);
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const FormButton = styled.div`
  margin-top: 1rem;
`;

export default ContactForm;
